import React, { useRef } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ScreenshotSectionProps {
  screenshot: string | null;
  screenshotError: string | null;
  isCapturingScreenshot: boolean;
  onFileUpload: (file: File) => void;
}

export const ScreenshotSection: React.FC<ScreenshotSectionProps> = ({
  screenshot,
  screenshotError,
  isCapturingScreenshot,
  onFileUpload
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const triggerUpload = () => fileInputRef.current?.click();

  return (
    <div className="metadata-item screenshot-item">
      <span className="metadata-label">{t('appFeedback.screenshot')}</span>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {screenshot ? (
        <div className="screenshot-container">
          <img
            src={screenshot}
            alt={t('appFeedback.uploadedScreenshot')}
            className="screenshot-preview"
          />
          <button
            type="button"
            className="retake-screenshot"
            onClick={triggerUpload}
            title={t('appFeedback.replaceScreenshot')}
          >
            <Upload size={14} />
            {t('appFeedback.replace')}
          </button>
        </div>
      ) : (
        <div className="screenshot-actions">
          {screenshotError && <span className="error-text">{screenshotError}</span>}
          <button
            type="button"
            className="capture-screenshot"
            onClick={triggerUpload}
            disabled={isCapturingScreenshot}
          >
            {isCapturingScreenshot ? <Loader2 size={14} className="loading-spinner" /> : <Upload size={14} />}
            {isCapturingScreenshot ? t('appFeedback.processing') : t('appFeedback.uploadScreenshot')}
          </button>
        </div>
      )}
    </div>
  );
};
